import { type HttpClient, query } from '../http';

import { criteriaParams } from '../games/games.params';

import {
  type EveningQuery,
  type RecommendationsQuery,
  type RecommendedGame,
  type EveningRecommendation,
  type RandomPickQuery,
  type RandomPickResult,
} from './recommendations.types';

/**
 * Recommendations.
 *
 * Warning: The four surfaces answer four different questions and must not be
 * swapped: `trending` is what the week is playing (nobody's taste),
 * `for-you` is scored against yours, `evening` is a labelled mix from your
 * own shelf, and the die draws one game to play now.
 *
 * @param http Transport supplied by the application.
 * @returns The `recommendations` domain of the API client.
 */
export function createRecommendationsApi(http: HttpClient) {
  return {
    /**
     * What the week is playing — the store's headline row.
     *
     * Warning: **Not personalised.** It ranks games by recent activity across
     * everybody (library adds, sessions started, wishlist adds), and falls
     * back to recently added catalogue entries when the week has been quiet.
     * Labelling it "for you" would be a lie the data cannot back.
     *
     * @param params How many games.
     * @returns The games, most active first.
     */
    getTrending: (params: RecommendationsQuery = {}) =>
      http.get<readonly RecommendedGame[]>(
        `/recommendations/trending${query({ limit: params.limit })}`,
      ),

    /**
     * Scored against the reader's own taste: what their shelf is made of,
     * what their friends own that they do not, and what they have played
     * without ever adding.
     *
     * Warning: Games already owned or already wishlisted are excluded by the
     * server, so this row never suggests something the reader has.
     *
     * @param params How many games.
     * @returns The games, best match first.
     */
    getForYou: (params: RecommendationsQuery = {}) =>
      http.get<readonly RecommendedGame[]>(
        `/recommendations/for-you${query({ limit: params.limit })}`,
      ),

    /**
     * Reads what the dashboard suggests for tonight: a handful of games from
     * the household's shelf, each drawn for a theme of its own.
     *
     * Warning: The mix changes on every call — themes are drawn at random,
     * weighted by what the caller actually plays. Do not expect two reads to
     * agree, and do not poll it behind the reader's back.
     *
     * @param params How many games to gather.
     * @returns The mix, or `null` when the shelf holds nothing suggestible.
     */
    getEvening: (params: EveningQuery = {}) =>
      http.get<EveningRecommendation>(
        `/recommendations/evening${query({ count: params.count })}`,
      ),

    /**
     * Draws one game from a shelf: the household's by default, the caller's
     * own with `scope: 'mine'`.
     *
     * Accepts the **same criteria as the library** — they share
     * `criteriaParams`, so a filter added there reaches the draw too, which is
     * what lets the Sessions screen reuse the library's filter panel whole.
     *
     * Warning: Answers `null` when nothing matches. That is a normal state, not
     * a failure: an empty collection and over-tight filters both look like it.
     *
     * @param params Game criteria, plus which shelf to draw from.
     * @returns A game, or `null`.
     * @throws `recommendation.error.random_pick.not_in_family` (400) for
     *   `scope: 'family'` without a household.
     */
    getRandomPick: (params: RandomPickQuery = {}) =>
      http.get<RandomPickResult>(
        `/recommendations/random-pick${query({
          ...criteriaParams(params),
          scope: params.scope,
          neverPlayed: params.neverPlayed,
        })}`,
      ),
  };
}

export type RecommendationsApi = ReturnType<typeof createRecommendationsApi>;
